// API utility with automatic authentication
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Get auth token from localStorage
const getAuthToken = () => {
  const savedAuth = localStorage.getItem('glanalyzer_auth');
  if (savedAuth) {
    try {
      const authData = JSON.parse(savedAuth);
      return authData.token;
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }
  return null;
};

// Token refresh in progress flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise = null;

// Refresh token function
const refreshToken = async () => {
  if (isRefreshing) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const currentToken = getAuthToken();
      if (!currentToken) {
        throw new Error('No token to refresh');
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      
      if (data.success && data.token) {
        // Update localStorage with new token
        localStorage.setItem('glanalyzer_auth', JSON.stringify({
          token: data.token,
          user: data.user,
          timestamp: Date.now()
        }));
        return data.token;
      } else {
        throw new Error('Invalid refresh response');
      }
    } catch (error) {
      // If refresh fails, clear auth and redirect to login
      localStorage.removeItem('glanalyzer_auth');
      window.location.reload();
      throw error;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Create authenticated fetch wrapper
const apiRequest = async (url, options = {}) => {
  const token = getAuthToken();

  const isFormData = options.body instanceof FormData;

  const config = {
    ...options,
    headers: {
      // Only set JSON content type when not sending FormData
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...options.headers,
    },
  };

  // Add authorization header if token exists
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const fullUrl = url.startsWith('/') ? `${API_BASE_URL}${url}` : url;

  try {
    const response = await fetch(fullUrl, config);

    // Handle 401 unauthorized - try token refresh
    if (response.status === 401 && token && !options._isRetry) {
      try {
        const newToken = await refreshToken();
        
        // Retry the original request with new token
        const retryConfig = {
          ...config,
          headers: {
            ...config.headers,
            Authorization: `Bearer ${newToken}`
          },
          _isRetry: true // Prevent infinite retry loops
        };
        
        return await apiRequest(url, retryConfig);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // If refresh fails, the refreshToken function already handles cleanup
        const error = new Error('Authentication failed');
        error.response = { status: 401 };
        throw error;
      }
    }

    // Parse response body safely
    const contentType = response.headers.get('content-type') || '';
    let data = null;

    if (response.status !== 204) {
      if (contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch {
          data = null;
        }
      } else {
        try {
          data = await response.text();
        } catch {
          data = null;
        }
      }
    }

    // Throw on non-OK to allow components to handle errors consistently
    if (!response.ok) {
      const error = new Error('Request failed');
      error.response = { status: response.status, data };
      throw error;
    }

    // Return axios-like shape
    return {
      status: response.status,
      data,
      headers: Object.fromEntries(response.headers.entries()),
    };
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
};

// Convenience methods
const api = {
  get: (url, options = {}) => apiRequest(url, { ...options, method: 'GET' }),
  post: (url, data, options = {}) => apiRequest(url, {
    ...options,
    method: 'POST',
    body: options.body instanceof FormData ? options.body : JSON.stringify(data),
  }),
  put: (url, data, options = {}) => apiRequest(url, {
    ...options,
    method: 'PUT',
    body: options.body instanceof FormData ? options.body : JSON.stringify(data),
  }),
  delete: (url, options = {}) => apiRequest(url, { ...options, method: 'DELETE' }),
};

export default api;
export { apiRequest, getAuthToken };